import { getAgentOpenAISettings } from '@/utils/agentEncryption'
import { fetchDataSourceDirect } from '@/stores/dataSourceStore'
import { encryptData, decryptData } from '@/utils/encryption'
import { v7 as uuidv7 } from 'uuid'
import { OpenAPI } from './api/core/OpenAPI'

export interface AIAssistantRequest {
  datasourceId: string
  message: string
  model?: string
  useLangChain?: boolean
  autonomousMode?: boolean
  maxIterations?: number
  context?: {
    database?: string
    schema?: string
    tables?: string[]
    connectionType?: string
  }
}

export interface AIAssistantResponse {
  content: string
  sqlQuery?: string
  error?: string
}

export interface AIAssistantStreamResponse {
  type: 'content' | 'sql' | 'error' | 'done'
  data: string
}

export type AIAssistantStreamCallback = (chunk: { 
  content?: string; 
  sqlQuery?: string; 
  explanation?: string;
  error?: string; 
  done?: boolean;
  eventType?: string;
  thinking?: string;
  queryResults?: { query: string; results: unknown; executionTime?: number }[];
  agentTrace?: {
    tool?: string;
    actionInput?: string;
    observation?: string;
  };
  agentTraces?: {
    action?: string;
    actionInput?: string;
    observation?: string;
  }[];
  // Additional autonomous step metadata
  icon?: string;
  agentAction?: string;
  toolName?: string;
  tool?: string;
  toolInputPreview?: string;
  stepCount?: number;
  serverTimestamp?: number;
}) => void

/**
 * Service for AI Assistant functionality using the ASK_AI agent proxy command
 */
export class AIAssistantService {
  /**
   * Check if OpenAI API key is configured for a datasource
   */
  static async hasOpenAIKey(datasourceId: string): Promise<boolean> {
    try {
      const datasource = await fetchDataSourceDirect(datasourceId)
      if (!datasource) {
        console.warn('Datasource not found:', datasourceId)
        return false
      }
      
      if (!datasource.agentId) {
        console.warn('No agent ID found for datasource:', datasourceId)
        return false
      }
      
      const openaiSettings = getAgentOpenAISettings(datasource.agentId)
      return openaiSettings.enabled && !!openaiSettings.apiKey.trim()
    } catch (error) {
      console.error('Failed to check OpenAI key availability:', error)
      return false
    }
  }
  
  /**
   * Send a message to AI Assistant with real-time streaming
   */
  static async sendMessageStream(
    request: AIAssistantRequest, 
    onChunk: AIAssistantStreamCallback
  ): Promise<void> {
    try {
      // Get the datasource and OpenAI API key
      const datasource = await fetchDataSourceDirect(request.datasourceId)
      if (!datasource) {
        throw new Error('Datasource not found')
      }
      
      if (!datasource.agentId) {
        throw new Error('No agent ID found for this datasource')
      }
      
      const openaiSettings = getAgentOpenAISettings(datasource.agentId)
      if (!openaiSettings.enabled || !openaiSettings.apiKey.trim()) {
        throw new Error('OpenAI API key not configured for this agent')
      }
      
      // Get available table names for context (if any)
      const tableNames = request.context?.tables || []
      
      // Enhance question with context for better AI understanding (connection type and schema)
      const enhancedQuestion = this.enhanceQuestionWithContext(request.message, request.context)
      
      // Build the command data for ASK_AI following the exact API spec
      const selectedModel = request.model || 'gpt-4o'
      const commandData = {
        question: enhancedQuestion, // Enhanced question with connection type context (schema handled separately for PostgreSQL)
        table_names: tableNames,
        database_name: request.context?.database, // Add database_name field like other commands
        schema_name: request.context?.connectionType === 'postgresql' ? request.context?.schema : undefined, // Add schema_name as separate field for PostgreSQL
        api_key: openaiSettings.apiKey,
        model: selectedModel, // Use selected model or default to gpt-4o
        use_langchain: true, // Always enabled for enhanced SQL generation
        autonomous_mode: request.autonomousMode || false, // Enable autonomous mode for automatic query execution
        max_iterations: request.maxIterations || 10, // Maximum number of autonomous iterations
        max_tokens: 1000,
        // GPT-5 doesn't support temperature 0.0, only the default (1) value
        ...(selectedModel !== 'gpt-5' && { temperature: 0.0 }),
        // GPT-5 doesn't support explicit streaming parameter, only the default behavior
        ...(selectedModel !== 'gpt-5' && { streaming: true })
      }
      
      console.log('🤖 [AI Assistant] Starting real-time streaming:', {
        datasourceId: request.datasourceId,
        message: request.message,
        context: request.context
      })
      
      // Execute streaming command with real-time callbacks
      await this.executeStreamingAskAICommand(request.datasourceId, commandData, onChunk)
      
    } catch (error) {
      console.error('AI Assistant streaming request failed:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to get AI response'
      onChunk({ error: errorMessage, done: true })
    }
  }

  /**
   * Send a message to AI Assistant and get a response (legacy method)
   */
  static async sendMessage(request: AIAssistantRequest): Promise<AIAssistantResponse> {
    try {
      // Get the datasource and OpenAI API key
      const datasource = await fetchDataSourceDirect(request.datasourceId)
      if (!datasource) {
        throw new Error('Datasource not found')
      }
      
      if (!datasource.agentId) {
        throw new Error('No agent ID found for this datasource')
      }
      
      const openaiSettings = getAgentOpenAISettings(datasource.agentId)
      if (!openaiSettings.enabled || !openaiSettings.apiKey.trim()) {
        throw new Error('OpenAI API key not configured for this agent')
      }
      
      // Get available table names for context (if any)
      const tableNames = request.context?.tables || []
      
      // Enhance question with context for better AI understanding (connection type and schema)
      const enhancedQuestion = this.enhanceQuestionWithContext(request.message, request.context)
      
      // Build the command data for ASK_AI following the exact API spec
      const selectedModel = request.model || 'gpt-4o'
      const commandData = {
        question: enhancedQuestion, // Enhanced question with connection type context (schema handled separately for PostgreSQL)
        table_names: tableNames, // List of relevant table names
        database_name: request.context?.database, // Add database_name field like other commands
        schema_name: request.context?.connectionType === 'postgresql' ? request.context?.schema : undefined, // Add schema_name as separate field for PostgreSQL
        api_key: openaiSettings.apiKey, // OpenAI API key
        model: selectedModel, // Use selected model or default to gpt-4o
        use_langchain: true, // Always enabled for enhanced SQL generation
        autonomous_mode: request.autonomousMode || false, // Enable autonomous mode for automatic query execution
        max_iterations: request.maxIterations || 10, // Maximum number of autonomous iterations
        max_tokens: 1000, // Reasonable limit for SQL generation
        // GPT-5 doesn't support temperature 0.0, only the default (1) value
        ...(selectedModel !== 'gpt-5' && { temperature: 0.0 }),
        // GPT-5 doesn't support explicit streaming parameter, only the default behavior
        ...(selectedModel !== 'gpt-5' && { streaming: true })
      }
      
      console.log('🤖 [AI Assistant] Sending request:', {
        datasourceId: request.datasourceId,
        message: request.message,
        context: request.context
      })
      
      // Use the existing agent proxy service to send ASK_AI command
      // Since ASK_AI might not be in the current command list, we'll use executeCommand directly
      const response = await this.executeAskAICommand(request.datasourceId, commandData)
      
      return this.parseAIResponse(response)
      
    } catch (error) {
      console.error('AI Assistant request failed:', error)
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to get AI response'
      return {
        content: 'Sorry, I encountered an error while processing your request. Please try again.',
        error: errorMessage
      }
    }
  }
  
  /**
   * Execute ASK_AI command with real-time streaming callbacks
   */
  private static async executeStreamingAskAICommand(
    datasourceId: string, 
    commandData: Record<string, unknown>, 
    onChunk: AIAssistantStreamCallback
  ): Promise<void> {
    try {
      console.log('🌊 [AI Assistant] Starting real-time streaming command:', commandData)
      
      // Get encryption settings for the datasource
      const { encryptionKey } = await this.getEncryptionSettings(datasourceId)
      
      // Build the command structure following the same pattern as AgentProxyService
      // Remove any existing nonce/timestamp to ensure fresh values for each request
      const { nonce: _nonce, timestamp: _timestamp, ...cleanCommandData } = commandData as Record<string, unknown> & { nonce?: string; timestamp?: number }
      
      const command = {
        type: 'ask_ai',
        timeout: 60, // AI requests may take longer
        data: {
          ...cleanCommandData,
          nonce: uuidv7(), // Always generate fresh nonce for each request
          timestamp: Math.floor(Date.now() / 1000) // Always generate fresh timestamp for each request
        }
      }
      
      console.log('🤖 [AI Assistant] Sending streaming command:', command)
      
      // Make the streaming request with real-time processing
      // Pass the command data so each request can generate its own fresh encrypted payload
      await this.makeRealTimeStreamingRequestFetch(datasourceId, cleanCommandData, encryptionKey, onChunk)
      
    } catch (error) {
      console.error('Failed to execute streaming ASK_AI command:', error)
      onChunk({ error: error instanceof Error ? error.message : 'Unknown error', done: true })
    }
  }

  /**
   * Execute ASK_AI command through agent proxy with real-time streaming (legacy method)
   */
  private static async executeAskAICommand(datasourceId: string, commandData: Record<string, unknown>): Promise<AIAssistantResponse> {
    try {
      console.log('🤖 [AI Assistant] Executing real ASK_AI streaming command:', commandData)
      
      // Get encryption settings for the datasource
      const { encryptionKey } = await this.getEncryptionSettings(datasourceId)
      
      // Build the command structure following the same pattern as AgentProxyService
      // Remove any existing nonce/timestamp to ensure fresh values for each request
      const { nonce: _nonce, timestamp: _timestamp, ...cleanCommandData } = commandData as Record<string, unknown> & { nonce?: string; timestamp?: number }
      
      const command = {
        type: 'ask_ai',
        timeout: 60, // AI requests may take longer
        data: {
          ...cleanCommandData,
          nonce: uuidv7(), // Always generate fresh nonce for each request
          timestamp: Math.floor(Date.now() / 1000) // Always generate fresh timestamp for each request
        }
      }
      
      console.log('🤖 [AI Assistant] Sending command:', command)
      
      // Make the streaming request
      const response = await this.makeStreamingRequest(datasourceId, cleanCommandData, encryptionKey)
      return response
      
    } catch (error) {
      console.error('Failed to execute ASK_AI command:', error)
      throw error
    }
  }
  
  /**
   * Enhance the user's question with minimal context information (connection type only)
   * Database name and schema are now handled via separate fields in the command structure
   */
  private static enhanceQuestionWithContext(
    question: string, 
    context?: AIAssistantRequest['context']
  ): string {
    if (!context) return question
    
    const contextParts: string[] = []
    
    // Add connection type for AI to understand SQL dialect
    if (context.connectionType) {
      contextParts.push(`Database type: ${context.connectionType}`)
    }
    
    // Schema is now handled via schema_name field for PostgreSQL, not in context string
    
    if (contextParts.length > 0) {
      return `${question}\n\nContext: ${contextParts.join(', ')}`
    }
    
    return question
  }
  
  /**
   * Parse AI response into structured format
   */
  private static parseAIResponse(response: unknown): AIAssistantResponse {
    try {
      // Handle different response formats
      if (typeof response === 'string') {
        return this.parseTextResponse(response)
      }
      
      if (response && typeof response === 'object') {
        // Handle ASK_AI specific response structure
        if ('type' in response && response.type === 'ask_ai') {
          const explanation = ('explanation' in response && typeof response.explanation === 'string') ? response.explanation : 
                              ('message' in response && typeof response.message === 'string') ? response.message : ''
          const sqlQuery = ('sql_query' in response && typeof response.sql_query === 'string') ? response.sql_query : undefined
          return {
            content: explanation,
            sqlQuery: sqlQuery
          }
        }
        
        // Handle error response
        if ('status' in response && response.status === 'error') {
          const errorMessage = ('message' in response && typeof response.message === 'string') ? response.message : 'Unknown error'
          return {
            content: 'Sorry, I encountered an error while processing your request.',
            error: errorMessage
          }
        }
      }
      
      // Fallback for unexpected response format
      return {
        content: 'I received an unexpected response format. Please try again.',
        error: 'Invalid response format'
      }
      
    } catch (error) {
      console.error('Failed to parse AI response:', error)
      return {
        content: 'Failed to parse AI response. Please try again.',
        error: error instanceof Error ? error.message : 'Parse error'
      }
    }
  }
  
  /**
   * Parse text response to extract SQL queries
   */
  private static parseTextResponse(text: string): AIAssistantResponse {
    // Look for SQL code blocks or SQL patterns
    const sqlPatterns = [
      /```sql\n([\s\S]*?)\n```/gi,
      /```\n((?:SELECT|INSERT|UPDATE|DELETE|CREATE|DROP|ALTER|SHOW|DESCRIBE)[\s\S]*?)\n```/gi,
      /(?:^|\n)((?:SELECT|INSERT|UPDATE|DELETE|CREATE|DROP|ALTER|SHOW|DESCRIBE)(?:[^;]|;(?!\s*$))*;?)/gmi
    ]
    
    let sqlQuery: string | undefined
    let content = text
    
    for (const pattern of sqlPatterns) {
      const match = pattern.exec(text)
      if (match && match[1]) {
        sqlQuery = match[1].trim()
        // Remove the SQL from the content to avoid duplication
        content = text.replace(match[0], '').trim()
        break
      }
    }
    
    return {
      content: content || text,
      sqlQuery
    }
  }
  

  /**
   * Make streaming request using fetch with fresh encrypted payload (with smart URL handling for dev/prod)
   */
  private static async makeRealTimeStreamingRequestFetch(
    datasourceId: string, 
    commandData: Record<string, unknown>, 
    encryptionKey: string | undefined,
    onChunk: AIAssistantStreamCallback
  ): Promise<void> {
    // Smart URL construction: use relative URLs in development to go through Vite proxy, full URLs in production
    const isDevelopment = OpenAPI.BASE.includes('localhost:8081')
    const streamingUrl = isDevelopment 
      ? `/api/agent-proxy/${datasourceId}/stream`  // Relative URL for Vite proxy
      : `${OpenAPI.BASE}/api/agent-proxy/${datasourceId}/stream`  // Full URL for production
    
    console.log('🌊 [AI Assistant] Starting REAL-TIME streaming request to:', streamingUrl)
    console.log('🔧 [AI Assistant] Development mode:', isDevelopment)
    console.log('🔧 [AI Assistant] OpenAPI.BASE:', OpenAPI.BASE)
    
    try {
      // Generate fresh command with new nonce and timestamp for this request
      const command = {
        type: 'ask_ai',
        timeout: 60,
        data: {
          ...commandData,
          nonce: uuidv7(), // Fresh nonce for this specific request
          timestamp: Math.floor(Date.now() / 1000) // Fresh timestamp for this specific request
        }
      }
      
      console.log('🤖 [AI Assistant] Sending streaming command with fresh nonce:', command)
      
      // Encrypt the command payload with fresh nonce/timestamp
      let encryptedPayload: string
      if (encryptionKey) {
        // Convert command to plain object for encryption
        const commandObj: Record<string, unknown> = {
          type: command.type,
          timeout: command.timeout,
          data: command.data
        }
        encryptedPayload = await encryptData(commandObj, encryptionKey)
      } else {
        encryptedPayload = JSON.stringify(command)
      }
      
      // Build headers exactly like OpenAPI client does
      const apiRequestOptions = { method: 'POST' as const, url: `/api/agent-proxy/${datasourceId}/stream` }
      let token = ''
      if (typeof OpenAPI.TOKEN === 'function') {
        // OpenAPI.TOKEN is a Resolver function
        token = await OpenAPI.TOKEN(apiRequestOptions)
      } else if (typeof OpenAPI.TOKEN === 'string') {
        token = OpenAPI.TOKEN
      }
      
      // Build headers exactly like OpenAPI client
      const headers: Record<string, string> = {
        'Accept': 'text/event-stream',  // For streaming responses
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache'
      }
      
      // Add auth header exactly like OpenAPI client does
      if (token && token.trim()) {
        headers['Authorization'] = `Bearer ${token}`
      }
      
      // Build request exactly like OpenAPI client's sendRequest function
      const requestInit: RequestInit = {
        method: 'POST',
        headers: new Headers(headers),
        body: JSON.stringify({
          encrypted_payload: encryptedPayload
        })
      }
      
      // Add credentials exactly like OpenAPI client does
      if (OpenAPI.WITH_CREDENTIALS) {
        requestInit.credentials = OpenAPI.CREDENTIALS
      }
      
      const response = await fetch(streamingUrl, requestInit)
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      
      if (!response.body) {
        throw new Error('No response body for streaming')
      }
      
      // Process the streaming response with real-time callbacks
      await this.processRealTimeStreamingResponse(response.body, encryptionKey, onChunk)
      
    } catch (error) {
      console.error('🚨 [AI Assistant] Real-time streaming request failed:', error)
      onChunk({ error: `Streaming request failed: ${error instanceof Error ? error.message : 'Unknown error'}`, done: true })
    }
  }

  /**
   * Make a real-time streaming HTTP request to the agent proxy (legacy method)
   */
  private static async makeStreamingRequest(
    datasourceId: string, 
    commandData: Record<string, unknown>, 
    encryptionKey?: string
  ): Promise<AIAssistantResponse> {
    // Smart URL construction: use relative URLs in development to go through Vite proxy, full URLs in production
    const isDevelopment = OpenAPI.BASE.includes('localhost:8081')
    const streamingUrl = isDevelopment 
      ? `/api/agent-proxy/${datasourceId}/stream`  // Relative URL for Vite proxy
      : `${OpenAPI.BASE}/api/agent-proxy/${datasourceId}/stream`  // Full URL for production
    
    console.log('🌊 [AI Assistant] Starting streaming request to:', streamingUrl)
    console.log('🔧 [AI Assistant] Development mode:', isDevelopment)
    console.log('🔧 [AI Assistant] OpenAPI.BASE:', OpenAPI.BASE)
    
    try {
      // Generate fresh command with new nonce and timestamp for this request
      const command = {
        type: 'ask_ai',
        timeout: 60,
        data: {
          ...commandData,
          nonce: uuidv7(), // Fresh nonce for this specific request
          timestamp: Math.floor(Date.now() / 1000) // Fresh timestamp for this specific request
        }
      }
      
      console.log('🤖 [AI Assistant] Sending streaming command with fresh nonce:', command)
      
      // Encrypt the command payload with fresh nonce/timestamp
      let encryptedPayload: string
      if (encryptionKey) {
        // Convert command to plain object for encryption
        const commandObj: Record<string, unknown> = {
          type: command.type,
          timeout: command.timeout,
          data: command.data
        }
        encryptedPayload = await encryptData(commandObj, encryptionKey)
      } else {
        encryptedPayload = JSON.stringify(command)
      }
      
      // Build headers exactly like OpenAPI client does
      const apiRequestOptions = { method: 'POST' as const, url: `/api/agent-proxy/${datasourceId}/stream` }
      let token = ''
      if (typeof OpenAPI.TOKEN === 'function') {
        // OpenAPI.TOKEN is a Resolver function
        token = await OpenAPI.TOKEN(apiRequestOptions)
      } else if (typeof OpenAPI.TOKEN === 'string') {
        token = OpenAPI.TOKEN
      }
      
      // Build headers exactly like OpenAPI client
      const headers: Record<string, string> = {
        'Accept': 'text/event-stream',  // For streaming responses
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache'
      }
      
      // Add auth header exactly like OpenAPI client does
      if (token && token.trim()) {
        headers['Authorization'] = `Bearer ${token}`
      }
      
      // Build request exactly like OpenAPI client's sendRequest function
      const requestInit: RequestInit = {
        method: 'POST',
        headers: new Headers(headers),
        body: JSON.stringify({
          encrypted_payload: encryptedPayload
        })
      }
      
      // Add credentials exactly like OpenAPI client does
      if (OpenAPI.WITH_CREDENTIALS) {
        requestInit.credentials = OpenAPI.CREDENTIALS
      }
      
      const response = await fetch(streamingUrl, requestInit)
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      
      if (!response.body) {
        throw new Error('No response body for streaming')
      }
      
      // Process the streaming response
      return await this.processStreamingResponse(response.body, encryptionKey)
      
    } catch (error) {
      console.error('🚨 [AI Assistant] Streaming request failed:', error)
      throw new Error(`Streaming request failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }
  
  /**
   * Process the Server-Sent Events streaming response with real-time callbacks
   */
  private static async processRealTimeStreamingResponse(
    responseBody: ReadableStream<Uint8Array>, 
    encryptionKey: string | undefined,
    onChunk: AIAssistantStreamCallback
  ): Promise<void> {
    const reader = responseBody.getReader()
    const decoder = new TextDecoder()
    let chunkCount = 0
    let hasReceivedContent = false
    let hasReceivedError = false // Track if we've already sent an error
    
    console.log('🔄 [AI Assistant] Starting REAL-TIME chunk processing...')
    
    // Add timeout to prevent endless streaming
    const streamTimeout = setTimeout(() => {
      console.warn('⏰ [AI Assistant] Streaming timeout reached, forcing completion')
      onChunk({ 
        content: hasReceivedContent ? '' : 'Request timed out. Please try again.',
        done: true 
      })
    }, 30000) // 30 second timeout
    
    try {
      while (true) {
        const { done, value } = await reader.read()
        
        if (done) {
          console.log('✅ [AI Assistant] Real-time streaming completed')
          clearTimeout(streamTimeout)
          if (!hasReceivedError) {
            onChunk({ done: true })
          }
          break
        }
        
        const chunk = decoder.decode(value, { stream: true })
        chunkCount++
        
        console.log(`📦 [AI Assistant] Received chunk #${chunkCount}:`, {
          rawSize: chunk.length,
          preview: chunk.substring(0, 200) + (chunk.length > 200 ? '...' : '')
        })
        
        const lines = chunk.split('\n')
        
        for (const line of lines) {
          if (line.trim() === '') continue
          
          // Parse SSE format: "data: {json}"
          if (line.startsWith('data: ')) {
            const jsonData = line.substring(6) // Remove "data: " prefix
            
            try {
              const parsed = JSON.parse(jsonData) as unknown
              
              if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                console.log('📋 [AI Assistant] Parsed SSE message:', {
                  type: typeof parsed,
                  keys: Object.keys(parsed),
                  content: parsed
                })
                
                // Handle different SSE message types
                if ('status' in parsed && parsed.status === 'connected') {
                  console.log('🔗 [AI Assistant] Stream connected:', 'message' in parsed ? parsed.message : '')
                  continue
                }
                
                if ('streaming_complete' in parsed && parsed.streaming_complete === true) {
                  console.log('🏁 [AI Assistant] Streaming completed by server')
                  clearTimeout(streamTimeout)
                  if (!hasReceivedError) {
                    onChunk({ done: true })
                  }
                  return
                }
                
                if ('error' in parsed && parsed.error) {
                  console.error('❌ [AI Assistant] Stream error:', parsed)
                  clearTimeout(streamTimeout)
                  hasReceivedError = true
                  const errorMessage = ('message' in parsed && typeof parsed.message === 'string') ? parsed.message : 
                                       ('error' in parsed && typeof parsed.error === 'string') ? parsed.error : 'Unknown error'
                  onChunk({ error: errorMessage, done: true })
                  return
                }
                
                // Handle encrypted data chunks
                if ('data' in parsed && typeof parsed.data === 'string') {
                console.log('🔓 [AI Assistant] Decrypting chunk data...', {
                  encryptedSize: parsed.data.length,
                  hasEncryptionKey: !!encryptionKey,
                  encryptedPreview: `${parsed.data.substring(0, 100)}...`
                })
                
                const decryptedChunk = await this.decryptStreamChunk(parsed.data, encryptionKey)
                if (decryptedChunk) {
                  console.log('✨ [AI Assistant] Decrypted chunk successfully:', decryptedChunk)
                  
                  // Track that we've received content
                  if (decryptedChunk.content || decryptedChunk.sqlQuery) {
                    hasReceivedContent = true
                  }
                  
                  // Type guard for decrypted chunk - check properties directly
                  const dc = decryptedChunk
                  const chunkToSend: Parameters<AIAssistantStreamCallback>[0] = {
                    content: (dc && typeof dc === 'object' && 'content' in dc && typeof dc.content === 'string') ? dc.content : undefined,
                    sqlQuery: (dc && typeof dc === 'object' && 'sqlQuery' in dc && typeof dc.sqlQuery === 'string') ? dc.sqlQuery : undefined,
                    explanation: (dc && typeof dc === 'object' && 'explanation' in dc && typeof dc.explanation === 'string') ? dc.explanation : undefined,
                    eventType: (dc && typeof dc === 'object' && 'eventType' in dc && typeof dc.eventType === 'string') ? dc.eventType : undefined,
                    thinking: (dc && typeof dc === 'object' && 'thinking' in dc && typeof dc.thinking === 'string') ? dc.thinking : undefined,
                    queryResults: (dc && typeof dc === 'object' && 'queryResults' in dc && Array.isArray(dc.queryResults)) ? dc.queryResults : undefined,
                    agentTrace: (dc && typeof dc === 'object' && 'agentTrace' in dc && typeof dc.agentTrace === 'object' && dc.agentTrace !== null) ? dc.agentTrace : undefined,
                    agentTraces: (dc && typeof dc === 'object' && 'agentTraces' in dc && Array.isArray(dc.agentTraces)) ? dc.agentTraces : undefined,
                    error: (dc && typeof dc === 'object' && 'error' in dc && typeof dc.error === 'string') ? dc.error : undefined,
                    done: (dc && typeof dc === 'object' && 'done' in dc && typeof dc.done === 'boolean') ? dc.done : undefined
                  }
                  
                  console.log('📤 [AI Assistant] Sending chunk to UI callback:', chunkToSend)
                  
                  // Send the decrypted chunk to the callback immediately for real-time streaming
                  onChunk(chunkToSend)
                  
                  // If there's an error or the chunk is marked as done, terminate the stream
                  if (decryptedChunk.error || decryptedChunk.done) {
                    console.log('🔚 [AI Assistant] Terminating stream due to error or completion')
                    if (decryptedChunk.error) {
                      hasReceivedError = true
                    }
                    clearTimeout(streamTimeout)
                    return
                  }
                } else {
                  console.warn('⚠️ [AI Assistant] Failed to decrypt chunk or empty result')
                }
              }
              } // Close the if (parsed && typeof parsed === 'object') block
              
            } catch (parseError) {
              console.warn('⚠️ [AI Assistant] Failed to parse SSE data:', parseError, 'Raw line:', line)
            }
          }
        }
      }
    } finally {
      reader.releaseLock()
      clearTimeout(streamTimeout)
      console.log(`📊 [AI Assistant] Stream processing completed. Total chunks: ${chunkCount}, Had content: ${hasReceivedContent}`)
      
      // Ensure completion is always signaled (but not if we already sent an error)
      if (!hasReceivedContent && !hasReceivedError) {
        console.warn('⚠️ [AI Assistant] No content received, sending completion signal')
        onChunk({ done: true })
      }
    }
  }

  /**
   * Process the Server-Sent Events streaming response (legacy method)
   */
  private static async processStreamingResponse(
    responseBody: ReadableStream<Uint8Array>, 
    encryptionKey?: string
  ): Promise<AIAssistantResponse> {
    const reader = responseBody.getReader()
    const decoder = new TextDecoder()
    let accumulatedResponse = ''
    let sqlQuery: string | undefined
    let hasError = false
    let errorMessage = ''
    
    console.log('🔄 [AI Assistant] Processing streaming response chunks...')
    
    try {
      while (true) {
        const { done, value } = await reader.read()
        
        if (done) {
          console.log('✅ [AI Assistant] Streaming completed')
          break
        }
        
        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk.split('\n')
        
        for (const line of lines) {
          if (line.trim() === '') continue
          
          // Parse SSE format: "data: {json}"
          if (line.startsWith('data: ')) {
            const jsonData = line.substring(6) // Remove "data: " prefix
            
            try {
              const parsed = JSON.parse(jsonData) as unknown
              
              if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                // Handle different SSE message types
                if ('status' in parsed && parsed.status === 'connected') {
                  console.log('🔗 [AI Assistant] Stream connected:', 'message' in parsed ? parsed.message : '')
                  continue
                }
                
                if ('streaming_complete' in parsed && parsed.streaming_complete === true) {
                console.log('🏁 [AI Assistant] Streaming completed')
                break
              }
              
                if ('error' in parsed && parsed.error) {
                  console.error('❌ [AI Assistant] Stream error:', parsed)
                  hasError = true
                  errorMessage = ('message' in parsed && typeof parsed.message === 'string') ? parsed.message : 
                                 ('error' in parsed && typeof parsed.error === 'string') ? parsed.error : 'Unknown error'
                  break
                }
                
                // Handle encrypted data chunks
                if ('data' in parsed && typeof parsed.data === 'string') {
                  const decryptedChunk = await this.decryptStreamChunk(parsed.data, encryptionKey)
                  if (decryptedChunk) {
                    accumulatedResponse += decryptedChunk.content || ''
                    if (decryptedChunk.sqlQuery) {
                      const { sqlQuery: newSqlQuery } = decryptedChunk
                      sqlQuery = newSqlQuery
                    }
                  }
                }
              } // Close the if (parsed && typeof parsed === 'object') block
              
            } catch (parseError) {
              console.warn('⚠️ [AI Assistant] Failed to parse SSE data:', parseError, 'Raw line:', line)
            }
          }
        }
      }
    } finally {
      reader.releaseLock()
    }
    
    if (hasError) {
      return {
        content: 'Sorry, I encountered an error while processing your request.',
        error: errorMessage || 'Unknown streaming error'
      }
    }
    
    if (!accumulatedResponse.trim()) {
      return {
        content: 'I received an empty response. Please try rephrasing your question.',
        error: 'Empty response from AI service'
      }
    }
    
    return {
      content: accumulatedResponse.trim(),
      sqlQuery
    }
  }
  
  /**
   * Decrypt a streaming chunk if encryption is enabled
   */
  private static async decryptStreamChunk(
    encryptedData: string, 
    encryptionKey?: string
  ): Promise<{ content?: string; sqlQuery?: string; error?: string; done?: boolean } | null> {
    try {
      console.log('🔍 [AI Assistant] Attempting to decrypt chunk...', {
        hasEncryptionKey: !!encryptionKey,
        dataLength: encryptedData.length,
        dataPreview: `${encryptedData.substring(0, 50)}...`
      })
      
      let decryptedData: unknown
      
      if (encryptionKey) {
        console.log('🔐 [AI Assistant] Using encryption key to decrypt...')
        decryptedData = await decryptData(encryptedData, encryptionKey)
        console.log('🔓 [AI Assistant] Decryption result:', typeof decryptedData, decryptedData)
        
        if (typeof decryptedData === 'string') {
          decryptedData = JSON.parse(decryptedData)
        }
      } else {
        console.log('🔓 [AI Assistant] No encryption key, parsing as JSON...')
        decryptedData = JSON.parse(encryptedData)
      }
      
      console.log('📋 [AI Assistant] Parsed decrypted data:', {
        type: typeof decryptedData,
        keys: decryptedData && typeof decryptedData === 'object' ? Object.keys(decryptedData) : 'N/A',
        content: decryptedData
      })
      
      // Parse the decrypted chunk according to ASK_AI response format
      if (decryptedData && typeof decryptedData === 'object') {
        const chunk = decryptedData as Record<string, unknown>
        
        console.log('🎯 [AI Assistant] Processing chunk with keys:', Object.keys(chunk))
        
        // Handle ASK_AI specific response fields
        if (chunk.type === 'ask_ai') {
          console.log('✅ [AI Assistant] Found ASK_AI response type')
          
          // Check for error conditions first
          if (chunk.is_error === true || chunk.status === 'error') {
            console.log('❌ [AI Assistant] Error chunk detected:', chunk)
            const errorMessage = typeof chunk.message === 'string' ? chunk.message : 'An error occurred'
            const result = {
              content: undefined,
              sqlQuery: undefined,
              error: errorMessage,
              done: chunk.is_final === true
            }
            console.log('📤 [AI Assistant] Returning error result:', result)
            return result
          }

          // Special handling for final autonomous mode chunk
          if (chunk.is_final === true && chunk.autonomous_mode === true && chunk.data && typeof chunk.data === 'object') {
            const finalData = chunk.data as Record<string, unknown>
            console.log('🎯 [AI Assistant] Processing FINAL autonomous chunk:', {
              isFinal: chunk.is_final,
              autonomousMode: chunk.autonomous_mode,
              hasData: !!chunk.data,
              dataKeys: Object.keys(finalData),
              hasExplanation: typeof finalData.explanation === 'string',
              hasAgentTrace: Array.isArray(finalData.agent_trace),
              hasQueryResults: Array.isArray(finalData.query_results),
              explanation: finalData.explanation,
              agentTraceLength: Array.isArray(finalData.agent_trace) ? finalData.agent_trace.length : 0,
              queryResultsLength: Array.isArray(finalData.query_results) ? finalData.query_results.length : 0
            })

            // Parse final autonomous response
            const explanation = typeof finalData.explanation === 'string' ? finalData.explanation : undefined
            
            let agentTraces: { action?: string; actionInput?: string; observation?: string }[] | undefined
            if (Array.isArray(finalData.agent_trace)) {
              agentTraces = finalData.agent_trace.map((trace: unknown) => {
                const traceObj = trace as Record<string, unknown>
                return {
                  action: typeof traceObj.action === 'string' ? traceObj.action : undefined,
                  actionInput: typeof traceObj.action_input === 'string' ? traceObj.action_input : undefined,
                  observation: typeof traceObj.observation === 'string' ? traceObj.observation : undefined
                }
              })
            }

            let queryResults: { query: string; results: unknown; executionTime?: number }[] | undefined
            if (Array.isArray(finalData.query_results)) {
              queryResults = finalData.query_results.map((result: unknown) => {
                const resultObj = result as Record<string, unknown>
                return {
                  query: typeof resultObj.query === 'string' ? resultObj.query : '',
                  results: resultObj.result_preview || resultObj.results || null,
                  executionTime: typeof finalData.execution_time_ms === 'number' ? finalData.execution_time_ms : undefined
                }
              })
            }

            const finalResult = {
              content: explanation || '',
              explanation,
              agentTraces,
              queryResults,
              sqlQuery: typeof finalData.sql_query === 'string' ? finalData.sql_query : undefined,
              done: true
            }

            console.log('🎯 [AI Assistant] Final autonomous result:', {
              hasExplanation: !!finalResult.explanation,
              hasAgentTraces: !!finalResult.agentTraces,
              hasQueryResults: !!finalResult.queryResults,
              agentTracesCount: finalResult.agentTraces?.length || 0,
              queryResultsCount: finalResult.queryResults?.length || 0,
              finalResult
            })

            return finalResult
          }
          
          // Handle streaming lifecycle signals that should NOT end or surface content
          if ((chunk.streaming === true || typeof chunk.streaming_status === 'string')
              && chunk.streaming_status === 'streaming_started') {
            console.log('⏯️ [AI Assistant] Streaming started notification received; ignoring as content and continuing stream')
            return { content: undefined, sqlQuery: undefined, done: false }
          }
          
          // Check if this is a streaming chunk with nested data
          if (chunk.data && typeof chunk.data === 'object') {
            const chunkData = chunk.data as Record<string, unknown>
            console.log('🎯 [AI Assistant] Processing nested ASK_AI data:', chunkData)
            
            // Extract SQL query from accumulated content if it looks complete
            let sqlQuery: string | undefined
            if (typeof chunkData.accumulated_content === 'string') {
              const accContent = chunkData.accumulated_content
              // Look for SQL code blocks
              const sqlMatch = accContent.match(/```sql\n([\s\S]*?)\n```/)
              if (sqlMatch && sqlMatch[1]) {
                sqlQuery = sqlMatch[1].trim()
                console.log('🔍 [AI Assistant] Extracted SQL from accumulated content:', sqlQuery)
              }
            }
            
            const isStreamingFlow = chunk.streaming === true || typeof chunk.streaming_status === 'string'
            const isStreamCompleted = isStreamingFlow && (
              chunk.streaming_status === 'streaming_completed'
              || chunk.streaming_status === 'completed'
              || chunk.streaming_status === 'complete'
              || chunk.is_final === true
            )

            // Handle autonomous mode specific fields - try multiple possible field names
            const eventType = typeof chunkData.event_type === 'string' ? chunkData.event_type : 
                             (typeof chunkData.eventType === 'string' ? chunkData.eventType : undefined)
            
            const thinking = typeof chunkData.thinking === 'string' ? chunkData.thinking :
                           (typeof chunkData.thought === 'string' ? chunkData.thought :
                           (typeof chunkData.reasoning === 'string' ? chunkData.reasoning :
                           (typeof chunkData.agent_thought === 'string' ? chunkData.agent_thought : undefined)))
            
            const iconRaw = chunkData.icon
            const icon = typeof iconRaw === 'string' ? iconRaw : undefined
            const agentActionRaw = chunkData.agent_action
            const agentAction = typeof agentActionRaw === 'string' ? agentActionRaw : undefined
            const toolNameRaw = chunkData.tool_name
            const toolName = typeof toolNameRaw === 'string' ? toolNameRaw : undefined
            const toolRaw = chunkData.tool
            const tool = typeof toolRaw === 'string' ? toolRaw : undefined
            const toolInputPreviewRaw = chunkData.tool_input_preview
            const toolInputPreview = typeof toolInputPreviewRaw === 'string' ? toolInputPreviewRaw : undefined
            const stepCountRaw = chunkData.step_count
            const stepCount = typeof stepCountRaw === 'number' ? stepCountRaw : undefined
            const serverTimestampRaw = chunkData.timestamp
            const serverTimestamp = typeof serverTimestampRaw === 'number' ? serverTimestampRaw : undefined
            
            console.log('🧠 [AI Assistant] Parsing autonomous data:', {
              eventType,
              thinking: thinking ? `${thinking.substring(0, 100)}...` : undefined,
              hasQueryResults: Array.isArray(chunkData.query_results),
              hasAgentTrace: !!(chunkData.agent_trace && typeof chunkData.agent_trace === 'object'),
              hasExplanation: typeof chunkData.explanation === 'string',
              explanation: typeof chunkData.explanation === 'string' ? `${chunkData.explanation.substring(0, 100)}...` : undefined,
              allChunkDataKeys: Object.keys(chunkData),
              hasDirectContent: typeof chunkData.content === 'string',
              directContent: typeof chunkData.content === 'string' ? `${chunkData.content.substring(0, 100)}...` : undefined,
              isFinalChunk: chunk.is_final === true,
              rawChunkData: chunkData
            })
            
            // Parse query results for autonomous mode
            let queryResults: { query: string; results: unknown; executionTime?: number }[] | undefined
            if (Array.isArray(chunkData.query_results)) {
              queryResults = chunkData.query_results.map((result: unknown) => {
                const resultObj = result as Record<string, unknown>
                return {
                  query: typeof resultObj.query === 'string' ? resultObj.query : '',
                  results: resultObj.results || resultObj.observation || resultObj.result_preview || null,
                  executionTime: typeof resultObj.execution_time_ms === 'number' ? resultObj.execution_time_ms : undefined
                }
              })
            }
            
            console.log('🔍 [AI Assistant] Query results parsing:', {
              hasQueryResults: Array.isArray(chunkData.query_results),
              queryResultsLength: Array.isArray(chunkData.query_results) ? chunkData.query_results.length : 0,
              parsedQueryResults: queryResults,
              rawQueryResults: chunkData.query_results
            })

            // Parse agent trace information (can be single trace or array of traces)
            let agentTrace: { tool?: string; actionInput?: string; observation?: string } | undefined
            let agentTraces: Array<{ action?: string; actionInput?: string; observation?: string }> | undefined
            
            if (Array.isArray(chunkData.agent_trace)) {
              agentTraces = chunkData.agent_trace.map((trace: unknown) => {
                const traceObj = trace as Record<string, unknown>
                return {
                  action: typeof traceObj.action === 'string' ? traceObj.action : undefined,
                  actionInput: typeof traceObj.action_input === 'string' ? traceObj.action_input : undefined,
                  observation: typeof traceObj.observation === 'string' ? traceObj.observation : undefined
                }
              })
            } else if (chunkData.agent_trace && typeof chunkData.agent_trace === 'object') {
              const trace = chunkData.agent_trace as Record<string, unknown>
              agentTrace = {
                tool: typeof trace.tool === 'string' ? trace.tool : undefined,
                actionInput: typeof trace.action_input === 'string' ? trace.action_input : undefined,
                observation: typeof trace.observation === 'string' ? trace.observation : undefined
              }
            }
            
            // Parse explanation from final response
            const explanation = typeof chunkData.explanation === 'string' ? chunkData.explanation : undefined

            // For autonomous mode, separate thinking content from regular content
            let chunkContent: string | undefined
            if (typeof chunkData.chunk_content === 'string') {
              chunkContent = chunkData.chunk_content
            } else if (typeof chunkData.content === 'string') {
              // If this is an autonomous agent step event, treat content as thinking text
              const isAgentEvent = !!eventType
              chunkContent = isAgentEvent ? undefined : chunkData.content
            }

            const result = {
              content: chunkContent,
              sqlQuery: sqlQuery || (typeof chunkData.sql_query === 'string' ? chunkData.sql_query : undefined),
              explanation,
              eventType,
              // If eventType present and there's content, prefer showing it as thinking
              thinking: thinking ?? (eventType && typeof chunkData.content === 'string' ? chunkData.content : undefined),
              queryResults,
              agentTrace,
              agentTraces,
              icon,
              agentAction,
              toolName,
              tool,
              toolInputPreview,
              stepCount,
              serverTimestamp,
              done: isStreamingFlow ? isStreamCompleted : (chunk.is_final === true)
            }
            console.log('📤 [AI Assistant] Returning nested ASK_AI result:', {
              ...result,
              explanation: result.explanation ? `${result.explanation.substring(0, 100)}...` : undefined,
              agentTracesCount: result.agentTraces?.length || 0,
              queryResultsCount: result.queryResults?.length || 0
            })
            return result
          }
          
          // Fallback to direct fields for non-streaming responses
          const isStreamingFlow = chunk.streaming === true || typeof chunk.streaming_status === 'string'
          const isStreamCompleted = isStreamingFlow && (
            chunk.streaming_status === 'streaming_completed'
            || chunk.streaming_status === 'completed'
            || chunk.streaming_status === 'complete'
            || chunk.is_final === true
          )
          const isNonStreamingComplete = !isStreamingFlow && (
            (chunk.is_final === true)
            || (typeof chunk.status === 'string' && chunk.status === 'success')
            || (typeof chunk.finish_reason === 'string' && chunk.finish_reason === 'stop')
          )

          const directContent = typeof chunk.explanation === 'string'
            ? chunk.explanation
            : (typeof chunk.content === 'string'
              ? chunk.content
              : (typeof chunk.message === 'string' ? chunk.message : undefined))

          // Also try to extract thinking/content from the data field for autonomous mode
          let autonomousContent: string | undefined
          let autonomousEventType: string | undefined
          if (chunk.data && typeof chunk.data === 'object') {
            const dataObj = chunk.data as Record<string, unknown>
            autonomousContent = typeof dataObj.content === 'string' ? dataObj.content : undefined
            autonomousEventType = typeof dataObj.event_type === 'string' ? dataObj.event_type : undefined
          }

          // Extract final response data for autonomous mode
          let finalExplanation: string | undefined
          let finalAgentTraces: { action?: string; actionInput?: string; observation?: string }[] | undefined
          
          if (chunk.data && typeof chunk.data === 'object') {
            const dataObj = chunk.data as Record<string, unknown>
            finalExplanation = typeof dataObj.explanation === 'string' ? dataObj.explanation : undefined
            
            if (Array.isArray(dataObj.agent_trace)) {
              finalAgentTraces = dataObj.agent_trace.map((trace: unknown) => {
                const traceObj = trace as Record<string, unknown>
                return {
                  action: typeof traceObj.action === 'string' ? traceObj.action : undefined,
                  actionInput: typeof traceObj.action_input === 'string' ? traceObj.action_input : undefined,
                  observation: typeof traceObj.observation === 'string' ? traceObj.observation : undefined
                }
              })
            }
          }

          const result = {
            content: directContent,
            thinking: autonomousContent,
            eventType: autonomousEventType,
            explanation: finalExplanation,
            agentTraces: finalAgentTraces,
            sqlQuery: typeof chunk.sql_query === 'string' ? chunk.sql_query : undefined,
            done: isStreamingFlow ? isStreamCompleted : isNonStreamingComplete
          }
          console.log('📤 [AI Assistant] Returning direct ASK_AI result:', result)
          return result
        }
        
        // Fallback to generic content parsing
        console.log('🔄 [AI Assistant] Using fallback content parsing')
        
        // Check for error conditions in fallback parsing
        if (chunk.is_error === true || chunk.status === 'error') {
          console.log('❌ [AI Assistant] Error chunk detected in fallback:', chunk)
          const errorMessage = typeof chunk.message === 'string' ? chunk.message : 'An error occurred'
          const result = {
            content: undefined,
            sqlQuery: undefined,
            error: errorMessage,
            done: chunk.is_final === true
          }
          console.log('📤 [AI Assistant] Returning fallback error result:', result)
          return result
        }
        
        const isStreamingFlow = chunk.streaming === true || typeof chunk.streaming_status === 'string'
        const genericIsFinal = isStreamingFlow
          ? (chunk.streaming_status === 'streaming_completed'
            || chunk.streaming_status === 'completed'
            || chunk.streaming_status === 'complete'
            || chunk.is_final === true)
          : ((chunk.is_final === true)
            || (typeof chunk.status === 'string' && chunk.status === 'success')
            || (typeof chunk.finish_reason === 'string' && chunk.finish_reason === 'stop'))

        // Extract autonomous mode content from fallback parsing
        let fallbackAutonomousContent: string | undefined
        let fallbackAutonomousEventType: string | undefined
        let fallbackExplanation: string | undefined
        let fallbackAgentTraces: { action?: string; actionInput?: string; observation?: string }[] | undefined
        
        if (chunk.data && typeof chunk.data === 'object') {
          const dataObj = chunk.data as Record<string, unknown>
          fallbackAutonomousContent = typeof dataObj.content === 'string' ? dataObj.content : undefined
          fallbackAutonomousEventType = typeof dataObj.event_type === 'string' ? dataObj.event_type : undefined
          fallbackExplanation = typeof dataObj.explanation === 'string' ? dataObj.explanation : undefined
          
          if (Array.isArray(dataObj.agent_trace)) {
            fallbackAgentTraces = dataObj.agent_trace.map((trace: unknown) => {
              const traceObj = trace as Record<string, unknown>
              return {
                action: typeof traceObj.action === 'string' ? traceObj.action : undefined,
                actionInput: typeof traceObj.action_input === 'string' ? traceObj.action_input : undefined,
                observation: typeof traceObj.observation === 'string' ? traceObj.observation : undefined
              }
            })
          }
        }

        const result = {
          content: typeof chunk.content === 'string' ? chunk.content : 
                  (typeof chunk.explanation === 'string' ? chunk.explanation : (typeof chunk.message === 'string' ? chunk.message : undefined)),
          thinking: fallbackAutonomousContent,
          eventType: fallbackAutonomousEventType,
          explanation: fallbackExplanation,
          agentTraces: fallbackAgentTraces,
          sqlQuery: typeof chunk.sql_query === 'string' ? chunk.sql_query : undefined,
          done: genericIsFinal
        }
        console.log('📤 [AI Assistant] Returning fallback result:', result)
        return result
      }
      
      console.warn('❌ [AI Assistant] Decrypted data is not an object:', decryptedData)
      return null
      
    } catch (error) {
      console.error('❌ [AI Assistant] Failed to decrypt stream chunk:', error, 'Raw data:', `${encryptedData.substring(0, 200)}...`)
      return null
    }
  }
  
  /**
   * Get encryption settings using the same method as AgentProxyService
   */
  private static async getEncryptionSettings(datasourceId: string): Promise<{ encryptionEnabled: boolean; encryptionKey: string }> {
    try {
      const datasource = await fetchDataSourceDirect(datasourceId)
      if (!datasource) {
        console.warn('Datasource not found:', datasourceId)
        return { encryptionEnabled: false, encryptionKey: '' }
      }
      
      if (!datasource.agentId) {
        console.warn('No agent ID found for datasource:', datasourceId)
        return { encryptionEnabled: false, encryptionKey: '' }
      }
      
      // Import encryption utilities dynamically to avoid circular dependencies
      const { getAgentEncryptionSettings } = await import('@/utils/agentEncryption')
      const agentEncryption = getAgentEncryptionSettings(datasource.agentId)
      
      return {
        encryptionEnabled: agentEncryption.enabled,
        encryptionKey: agentEncryption.key || ''
      }
    } catch (error) {
      console.warn('Failed to fetch encryption settings, using defaults:', error)
      return { encryptionEnabled: false, encryptionKey: '' }
    }
  }
}